import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/Button';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { apiWorker } from '../api/worker';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isReporting?: boolean;
  isReported?: boolean;
  ticketId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isReporting: false,
    isReported: false,
  };


  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Automatically recover from chunk load errors (usually caused by deploying a new version while the user has the app open)
    if (error.name === 'ChunkLoadError' || error.message.includes('dynamically imported module')) {
      window.location.reload();
      return;
    }

    this.reportError(error, errorInfo);
  }

  private async reportError(error: Error, errorInfo: ErrorInfo) {
    try {
      this.setState({ isReporting: true });
      
      // Capture non-sensitive client state snapshot (localStorage keys)
      const stateSnapshot: Record<string, any> = {};
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && !key.toLowerCase().includes('token') && !key.toLowerCase().includes('auth') && !key.toLowerCase().includes('clerk')) {
            stateSnapshot[key] = localStorage.getItem(key);
          }
        }
      } catch {
        // Snapshot capture is best-effort; never mask the original error.
      }

      const response = await apiWorker.post('/api/errors/report', {
        message: error.message,
        stack: error.stack || '',
        componentStack: errorInfo.componentStack || '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        stateSnapshot,
        timestamp: new Date().toISOString()
      });
      
      const data = response.data;

      this.setState({
        isReporting: false,
        isReported: true,
        ticketId: data.ticketId
      });
    } catch (err) {
      console.error('Failed to report error to team:', err);
      this.setState({ isReporting: false });
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 dark:bg-slate-950">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center dark:bg-slate-900">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center text-red-600 mx-auto mb-6">
              <AlertTriangle size={32} />
            </div>
            
            <h1 className="text-xl font-bold text-navy-900 mb-2">Something went wrong</h1>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              An unexpected error occurred in the application.
            </p>

            <div className="mb-6 p-4 rounded-xl text-left bg-gray-50 border border-gray-200 dark:bg-slate-950 dark:border-slate-900 space-y-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono block">Status Report</span>
              {this.state.isReporting && (
                <div className="flex items-center gap-2 text-xs text-blue-605 dark:text-blue-400">
                  <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin shrink-0" />
                  <span>Reporting incident to development team...</span>
                </div>
              )}
              {this.state.isReported && (
                <div className="space-y-1">
                  <p className="text-xs text-green-600 dark:text-green-400 font-bold flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
                    Admins Notified Successfully
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                    Incident report dispatched to development team (Ref: <span className="font-mono font-bold text-gray-800 dark:text-gray-200">{this.state.ticketId}</span>).
                  </p>
                </div>
              )}
              {!this.state.isReporting && !this.state.isReported && (
                <p className="text-[11px] text-red-500">
                  Automatic incident report failed to submit.
                </p>
              )}
            </div>

            {this.state.error && (
              <div className="bg-gray-50 rounded-xl p-4 text-xs font-mono text-gray-600 text-left mb-8 overflow-auto max-h-32 dark:bg-slate-950">
                {this.state.error.message}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button 
                variant="primary" 
                onClick={() => window.location.reload()}
                className="w-full"
              >
                <RefreshCcw size={16} className="mr-2" />
                Reload Application
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                <Home size={16} className="mr-2" />
                Go to Homepage
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
